import React, { useEffect, useState } from "react";
import Axios from "../../services/AxiosInterceptor";
import { User, Mail, Phone, MapPin, Edit3, Save, X, Loader2 } from "lucide-react";
import "./Profile.css";

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const email = localStorage.getItem("email");
        if (!email) {
          console.error("No email found in localStorage");
          return;
        }
        const response = await Axios.get(`api/get_user?email=${email}`);
        setUser(response.data.data);
        setForm(response.data.data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handleSave = async () => {
    if (!form.username?.trim() || !form.mobile_no?.trim() || !form.location?.trim()) {
      alert("Please fill in all fields.");
      return;
    }
    setSaving(true);
    try {
      const response = await Axios.put("api/update_user", form);
      setUser(response.data.data);
      setForm(response.data.data);
      setEditing(false);
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(user);
    setEditing(false);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0" }}>
        <Loader2 size={28} style={{ animation: "spin 0.8s linear infinite", color: "var(--ym-sage)" }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="ym-card ym-card-pad" style={{ textAlign: "center", padding: "60px 24px" }}>
        <p className="ym-text" style={{ color: "var(--ym-muted)", margin: 0 }}>User data not found.</p>
      </div>
    );
  }

  const fields = [
    { key: "username", label: "Full Name", icon: User, placeholder: "Enter your name" },
    { key: "email", label: "Email Address", icon: Mail, disabled: true },
    { key: "mobile_no", label: "Mobile Number", icon: Phone, placeholder: "Enter mobile number", pattern: "[0-9]{10}" },
    { key: "location", label: "Location", icon: MapPin, placeholder: "Enter your location" },
  ];

  return (
    <div>
      {/* Header Card */}
      <div className="ym-card" style={{ marginBottom: 20, overflow: "hidden" }}>
        <div
          style={{
            padding: "28px 24px",
            display: "flex",
            alignItems: "center",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "linear-gradient(135deg, var(--ym-sage), #1b3027)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 32,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {(user.username || "U")[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h3 className="ym-service-title" style={{ fontSize: 20, margin: "0 0 4px" }}>{user.username}</h3>
            <p style={{ margin: 0, fontSize: 13, color: "var(--ym-muted)", display: "flex", alignItems: "center", gap: 6 }}>
              <Mail size={14} /> {user.email}
            </p>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="ym-button ym-button-primary"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <Edit3 size={16} />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Details Card */}
      <div className="ym-card ym-card-pad">
        <h4 className="ym-service-title" style={{ fontSize: 16, margin: "0 0 20px" }}>
          {editing ? "Edit Your Details" : "Profile Details"}
        </h4>

        {editing ? (
          <div>
            <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
              {fields.filter(f => !f.key.startsWith("email")).map((field) => (
                <div key={field.key}>
                  <label className="ym-label" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <field.icon size={14} />
                    {field.label}
                  </label>
                  <input
                    className="ym-input"
                    value={form[field.key] || ""}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    pattern={field.pattern}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "var(--ym-radius)",
                      border: "1px solid var(--ym-line)",
                      fontSize: 14,
                      fontFamily: "inherit",
                      outline: "none",
                      background: field.disabled ? "rgba(38,66,54,0.04)" : "#fff",
                      color: "var(--ym-ink)",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "var(--ym-gold)"; }}
                    onBlur={(e) => { e.target.style.borderColor = "var(--ym-line)"; }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <button
                onClick={handleCancel}
                className="ym-button ym-button-ghost"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="ym-button ym-button-gold"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                {saving ? <Loader2 size={16} style={{ animation: "spin 0.8s linear infinite" }} /> : <Save size={16} />}
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            {fields.map((field) => {
              const value = user[field.key];
              return (
                <div key={field.key} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "var(--ym-radius)",
                      background: "rgba(38,66,54,0.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--ym-sage)",
                      flexShrink: 0,
                    }}
                  >
                    <field.icon size={17} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "var(--ym-muted)", textTransform: "uppercase", letterSpacing: "0.03em" }}>{field.label}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 700, color: "var(--ym-ink)" }}>
                      {value || (field.disabled ? "" : "Not provided")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
